import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getCookie, setCookie, removeCookie } from "../../utils/cookie";
import { identifySentryUser } from "../../utils/sentryUser";
import { refreshCookieHeaders, setAccessToken } from "../../utils/tokenStore";
import { clearLegacyToken } from "../../utils/legacyToken";
import { ResetResult } from "../businessprofile/update";

const URL_API = process.env.URL_API;
const loginURL = `${URL_API}/identity/suppliers/login`;
const detailsURL = `${URL_API}/supplier-portal/details`;

export interface LoginResult {
  data: any[];
}

export interface DetailsResult {
  data: any[];
}

export const loginApi = createAsyncThunk(
  "account/login",
  async (account: any, { rejectWithValue }) => {
    try {
      // sellsmart-api#4626: the API sets the HttpOnly refresh cookie, so the
      // request carries credentials; `remember` decides whether the cookie
      // outlives the browser session.
      const { data } = await axios.post<LoginResult>(
        loginURL,
        { ...account, remember: Boolean(account.keepLoggedIn) },
        { withCredentials: true, headers: refreshCookieHeaders() },
      );

      // get the current date
      const d = new Date();

      // get the current time in milliseconds format
      const currentTime = d.getTime();

      const cookieOptions = account.keepLoggedIn
        ? { expires: new Date(currentTime + 60 * 60 * 24 * 30 * 1000) }
        : {};

      const { user, accessToken }: any = data.data;

      if (accessToken) {
        const details_response: any = await axios.get<DetailsResult>(
          detailsURL,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        //@ts-ignore
        const businessProfileID = details_response.data.data.id;
        const permissions = details_response.data.data.account.permissions;

        //@ts-ignore
        const defaultQuoteExpiry = details_response.data.data.quoteSettings[0];
        const termsAccepted = details_response.data.data.termsAccepted;
        const supplierCategories =
          details_response.data.data.supplierCategories;

        if (account.keepLoggedIn) {
          setCookie("keepLoggedIn", account.keepLoggedIn, cookieOptions);
        } else {
          removeCookie("keepLoggedIn");
        }

        setCookie("authenticated", true, cookieOptions);
        setCookie("auth", user, cookieOptions);
        // In memory only (ADR-2); the old readable cookie is never written again.
        setAccessToken(accessToken);
        clearLegacyToken();
        identifySentryUser(user);
        setCookie("lastActive", currentTime, cookieOptions);
        setCookie("quoteSetting", defaultQuoteExpiry, cookieOptions);
        setCookie("businessProfileID", businessProfileID, cookieOptions);
        setCookie("permissions", permissions, cookieOptions);
        setCookie("termsAccepted", termsAccepted, cookieOptions);
        setCookie(
          "role",
          details_response.data.data.account.role,
          cookieOptions,
        );

        const filteredData = supplierCategories.map(
          (category: { id: string; name: string }) => ({
            id: category.id,
            name: category.name,
          }),
        );

        setCookie("categories", filteredData, cookieOptions);

        if (details_response.data.data.hasBankAccount) {
          const bankAccountID = details_response.data.data.bankAccount.id;
          setCookie("bankAccountID", bankAccountID, cookieOptions);
        }

        if (
          details_response.data.data.account.permissions.includes(
            "CanManageBankAccountDetails",
          )
        ) {
          setCookie("canManageBanks", "yes", cookieOptions);
        } else {
          setCookie("canManageBanks", "no", cookieOptions);
        }
      }

      return data;
    } catch (error) {
      // @ts-ignore
      return rejectWithValue(error.response.data);
    }
  },
);
